import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    Utils,
    fetchWithAuth,
    filtersObjectHasAnyValue,
    findOptionByValue,
    SENT_MESSAGE_DELIVERY_STATUS,
    MESSAGE_CREATED_BY_OPTIONS,
    MESSAGE_AUDIENCE_OPTIONS,
    MESSAGE_SEND_KIND_OPTIONS,
    formatDateTime,
    getDefaultPageSize,
    getTotalPages,
    can,
    getBrowserTimeZone,
    exportListCsv,
} from '../../utils';
import MessageReport from './MessageReport';
import SentMessagesFiltersBar from './SentMessagesFiltersBar';
import { useActionDropdown } from '../../hooks/useActionDropdown';
import { useTableMaxHeight } from '../../hooks/useTableMaxHeight';
import { useScrollEndDetection } from '../../hooks/useScrollEndDetection';
import ListPaginationBar from '../common/ListPaginationBar';
import { MdKeyboardArrowDown, MdAddCircle, MdFileDownload } from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import Swal from "sweetalert2";

const EMPTY_SENT_FILTERS = { notificationId: '', status: '', sentFrom: '', platform: '', audience_type: '', sendKind: '', dateFrom: '', dateTo: '', datePreset: '' };

const SentMessages = () => {
    const { appId, notificationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draftFilters, setDraftFilters] = useState(() => ({
        notificationId: searchParams.get('notificationId') || '',
        status: searchParams.get('status') || '',
        sentFrom: searchParams.get('sentFrom') || '',
        platform: searchParams.get('platform') || '',
        audience_type: searchParams.get('audience_type') || '',
        sendKind: searchParams.get('sendKind') || '',
        dateFrom: searchParams.get('date_from') || '',
        dateTo: searchParams.get('date_to') || '',
        datePreset: searchParams.get('preset') || '',
    }));
    const [appliedFilters, setAppliedFilters] = useState(() => ({
        notificationId: searchParams.get('notificationId') || '',
        status: searchParams.get('status') || '',
        sentFrom: searchParams.get('sentFrom') || '',
        platform: searchParams.get('platform') || '',
        audience_type: searchParams.get('audience_type') || '',
        sendKind: searchParams.get('sendKind') || '',
        dateFrom: searchParams.get('date_from') || '',
        dateTo: searchParams.get('date_to') || '',
        datePreset: searchParams.get('preset') || '',
    }));
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(() =>
        Math.max(1, parseInt(searchParams.get('page') || '1', 10))
    );
    const pageSize = getDefaultPageSize();
    const [messagesTotal, setMessagesTotal] = useState(0);
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    const getSentMessageActionOptionCount = () => 2 + (can("push.manage") ? 1 : 0);
    const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
      tableScrollRef,
      optionCount: getSentMessageActionOptionCount,
      menuWidth: 200,
    });
    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, messages?.length);

    const fetchSentMessages = async () => {
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'get/sent-messages', {
                method: 'POST',
                body: JSON.stringify({
                    app_id: appId,
                    notification_id: (appliedFilters.notificationId || '').trim()
                        ? appliedFilters.notificationId.trim()
                        : undefined,
                    sentFrom: appliedFilters.sentFrom !== '' && appliedFilters.sentFrom != null
                        ? appliedFilters.sentFrom
                        : undefined,
                    platform: (appliedFilters.platform || '').trim() ? appliedFilters.platform.trim() : undefined,
                    audience_type: appliedFilters.audience_type !== '' && appliedFilters.audience_type != null
                        ? appliedFilters.audience_type
                        : undefined,
                    sendKind: appliedFilters.sendKind !== '' && appliedFilters.sendKind != null
                        ? appliedFilters.sendKind
                        : undefined,
                    deliveryStatus: appliedFilters.status !== '' && appliedFilters.status != null
                        ? appliedFilters.status
                        : undefined,
                    ...(appliedFilters.dateFrom && appliedFilters.dateTo
                        ? {
                            date_from: appliedFilters.dateFrom,
                            date_to: appliedFilters.dateTo,
                            timezone: getBrowserTimeZone() || undefined,
                        }
                        : {}),
                    page: currentPage,
                    pageSize,
                }),
            });
            const result = await response.json();
            if (result.status) {
                setMessages(result.data || []);
                setMessagesTotal(result.meta?.total ?? (result.data?.length || 0));
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching sent messages:', error);
            setLoading(false);
        }
    };

    const handleNewMessage = () => {
        navigate(`/apps/${appId}/push-notification`);
    };

    useEffect(() => {
        if (!appId || notificationId) return;
        setLoading(true);
        fetchSentMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, notificationId, appliedFilters, currentPage]);

    useEffect(() => {
        if (!appId || notificationId) return;
        const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
        const nid = searchParams.get('notificationId') || '';
        const status = searchParams.get('status') || '';
        const sf = searchParams.get('sentFrom') || '';
        const pl = searchParams.get('platform') || '';
        const audience_type = searchParams.get('audience_type') || '';
        const sendKind = searchParams.get('sendKind') || '';
        const dateFrom = searchParams.get('date_from') || '';
        const dateTo = searchParams.get('date_to') || '';
        const datePreset = searchParams.get('preset') || '';
        setCurrentPage(page);
        setAppliedFilters({ notificationId: nid, status, sentFrom: sf, platform: pl, audience_type, sendKind, dateFrom, dateTo, datePreset });
        setDraftFilters({ notificationId: nid, status, sentFrom: sf, platform: pl, audience_type, sendKind, dateFrom, dateTo, datePreset });
    }, [appId, notificationId, searchParams]);

    const writeListUrl = (page, filters) => {
        const p = new URLSearchParams();
        if (page > 1) p.set('page', String(page));
        if (filters.notificationId?.trim()) p.set('notificationId', filters.notificationId.trim());
        if (filters.status !== '' && filters.status != null) p.set('status', String(filters.status));
        if (filters.sentFrom !== '' && filters.sentFrom != null) p.set('sentFrom', String(filters.sentFrom));
        if (filters.platform?.trim()) p.set('platform', filters.platform.trim());
        if (filters.audience_type !== '' && filters.audience_type != null) p.set('audience_type', String(filters.audience_type));
        if (filters.sendKind !== '' && filters.sendKind != null) p.set('sendKind', String(filters.sendKind));
        if (filters.dateFrom?.trim()) p.set('date_from', filters.dateFrom.trim());
        if (filters.dateTo?.trim()) p.set('date_to', filters.dateTo.trim());
        if ((filters.datePreset || '').trim() && filters.datePreset !== 'custom') {
            p.set('preset', filters.datePreset.trim());
        }
        setSearchParams(p, { replace: true });
    };

    const goListPage = (p) => {
        setCurrentPage(p);
        writeListUrl(p, appliedFilters);
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
        writeListUrl(1, draftFilters);
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_SENT_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    const totalRows = messagesTotal || 0;
    const totalPages = getTotalPages(totalRows, pageSize);
    const safePage = Math.min(Math.max(1, currentPage), totalPages);


    const handleViewReport = (message) => {
        closeDropdown();
        const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
        const qs = new URLSearchParams({
            activity_page: '1',
            activity_limit: String(pageSize),
            activity_filter: 'all',
            returnTo,
        });
        navigate(`/apps/${appId}/sent-messages/${message.notification_id}/report?${qs.toString()}`, {
            state: {
                listMessage: {
                    name: message.name,
                    title: message.title,
                    status: message.status,
                    sent_count: message.sent_count,
                    click_count: message.click_count,
                    ctr: message.ctr,
                    sentFrom: message.sentFrom,
                    audience_type: message.audience_type,
                    segment_names: message.segment_names || [],
                    sent_at: message.sent_at,
                    created_at: message.created_at,
                },
            },
        });
    };

    const handleCopyMessageId = (message) => {
        const messageId = message.notification_id;
        if (messageId) {
            navigator.clipboard.writeText(messageId);
            toast.success('Notification ID copied to clipboard!');
        } else {
            toast.warning('No notification ID available');
        }
    };

    const handleDeleteMessage = async (message) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you want to delete this message?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete",
        });
        if (!confirm.isConfirmed) return;

        try {
            const response = await fetchWithAuth(Utils.baseurl + 'delete/sent-message', {
                method: 'POST',
                body: JSON.stringify({
                    notification_id: message.notification_id,
                }),
            });
            const result = await response.json();
            if (result.status) {
                toast.success('Message deleted successfully');
                fetchSentMessages();
            } else {
                toast.error(result.message || 'Failed to delete message');
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Error deleting message');
        }
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv('get/sent-messages', {
                app_id: appId,
                notification_id: (appliedFilters.notificationId || '').trim() || undefined,
                sentFrom: appliedFilters.sentFrom !== '' && appliedFilters.sentFrom != null ? appliedFilters.sentFrom : undefined,
                platform: (appliedFilters.platform || '').trim() || undefined,
                audience_type: appliedFilters.audience_type !== '' && appliedFilters.audience_type != null ? appliedFilters.audience_type : undefined,
                sendKind: appliedFilters.sendKind !== '' && appliedFilters.sendKind != null ? appliedFilters.sendKind : undefined,
                deliveryStatus: appliedFilters.status !== '' && appliedFilters.status != null ? appliedFilters.status : undefined,
                ...(appliedFilters.dateFrom && appliedFilters.dateTo
                    ? {
                        date_from: appliedFilters.dateFrom,
                        date_to: appliedFilters.dateTo,
                        timezone: getBrowserTimeZone() || undefined,
                    }
                    : {}),
            }, "sent-messages.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    if (notificationId) {
        return (
            <MessageReport
                key={notificationId}
            />
        );
    }

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>Sent Messages</h1>
                    </div>
                    <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => { setLoading(true); fetchSentMessages(); }}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                        {can("push.manage") ? (
                            <button type="button" className="add_app_btn btn_theme_primary" onClick={handleNewMessage}>
                                <MdAddCircle />
                                <span>New Message</span>
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="sent-messages-filter-panel"
                        id="sent-messages-filter-accordion"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div
                            className="list-page-filter-accordion-body"
                            id="sent-messages-filter-panel"
                            role="region"
                            aria-labelledby="sent-messages-filter-accordion"
                        >
                            <SentMessagesFiltersBar
                                draftFilters={draftFilters}
                                setDraftFilters={setDraftFilters}
                                onSearch={handleApplyFilters}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </Collapse>
                </div>

                <div className="dashboard_app_table dashboard_app_table--list-records">
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th>Notification ID</th>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Sent At</th>
                                            <th>Sent</th>
                                            <th>CTR</th>
                                            <th>Audience</th>
                                            <th>Segments</th>
                                            <th>Type</th>
                                            <th>Source</th>
                                            <th className="col-action" style={{ textAlign: 'right' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="11" className="text-center">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : messages.length === 0 ? (
                                            <tr>
                                                <td colSpan="11" className="text-center">
                                                    No messages found
                                                </td>
                                            </tr>
                                        ) : (
                                            messages.map((message, index) => {
                                                const createdOpt = findOptionByValue(
                                                    MESSAGE_CREATED_BY_OPTIONS,
                                                    message.sentFrom
                                                );
                                                const statusOpt = findOptionByValue(
                                                    SENT_MESSAGE_DELIVERY_STATUS,
                                                    message.status
                                                );
                                                const audienceOpt = findOptionByValue(
                                                    MESSAGE_AUDIENCE_OPTIONS,
                                                    message.audience_type
                                                );
                                                const segmentNames = message.segment_names || [];
                                                const sendKindOpt = findOptionByValue(
                                                    MESSAGE_SEND_KIND_OPTIONS,
                                                    message.sendKind
                                                );
                                                return (
                                                    <tr key={index}>
                                                        <td className="truncate">{message.notification_id || '-'}</td>
                                                        <td className="message-name">{message.name || message.title}</td>
                                                        <td>
                                                            {statusOpt ? (
                                                                <span className={statusOpt.badgeClass}>{statusOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>{formatDateTime(message.sent_at || message.created_at)}</td>
                                                        <td>{message.sent_count != null ? message.sent_count : 0}</td>
                                                        <td>{message.ctr || '0.00%'}</td>
                                                        <td>
                                                            {audienceOpt ? (
                                                                <span className={audienceOpt.badgeClass}>{audienceOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {segmentNames.length ? (
                                                                <span className="truncate" title={segmentNames.join(', ')}>
                                                                    {segmentNames.join(', ')}
                                                                </span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {sendKindOpt ? (
                                                                <span className={sendKindOpt.badgeClass}>{sendKindOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {createdOpt ? (
                                                                <span className={createdOpt.badgeClass}>{createdOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td className="col-action" style={{ textAlign: 'right' }}>
                                                            <button
                                                                type="button"
                                                                className="dropdown-trigger-btn btn btn-link p-0"
                                                                aria-label="Actions"
                                                                onClick={(e) => handleOpenDropdown(e, message.notification_id, message)}
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                                    <circle cx="8" cy="3" r="1.5" />
                                                                    <circle cx="8" cy="8" r="1.5" />
                                                                    <circle cx="8" cy="13" r="1.5" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={safePage}
                                setPage={goListPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Sent messages pagination"
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {openActionItem &&
                dropdownPosition &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'fixed',
                            left: dropdownPosition.left,
                            top: dropdownPosition.top,
                            zIndex: 10000,
                            minWidth: '220px',
                        }}
                    >
                        <div className="dropdown-menu-subscription">
                            <button
                                type="button"
                                onClick={() => handleViewReport(openActionItem)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M4 0h8a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V2a2 2 0 012-2zm0 1a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V2a1 1 0 00-1-1H4z" />
                                    <path d="M4 4h8v1H4V4zm0 3h8v1H4V7zm0 3h5v1H4v-1z" />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>View Report</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    closeDropdown();
                                    handleCopyMessageId(openActionItem);
                                }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M4 2a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V2zm2-1a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V2a1 1 0 00-1-1H6z" />
                                    <path d="M2 6a2 2 0 012-2v10h10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>Copy Message ID</span>
                            </button>
                            {can("push.manage") ? (
                            <button
                                type="button"
                                className="delete-class"
                                onClick={() => {
                                    closeDropdown();
                                    handleDeleteMessage(openActionItem);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    color: 'red',
                                }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ marginRight: '8px', flexShrink: 0 }}
                                >
                                    <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
                                    <path
                                        fillRule="evenodd"
                                        d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                                    />
                                </svg>
                                <span style={{ fontSize: '0.875rem' }}>Delete</span>
                            </button>
                            ) : null}
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default SentMessages;
